import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";

import { auth } from "../../firebase.js";
import { setStatsOptOut } from "../../emailPrefs.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";

/**
 * Unsubscribe landing for the stats email (List-Unsubscribe target).
 *
 * To keep it secure with per-user Firestore rules, the opt-out is written
 * for the logged-in account. Recipients of the admin portal already have an
 * account; if they are not logged in we ask them to log in first, after
 * which they can also manage this under Account > E-mailvoorkeuren.
 */
function Unsubscribe() {
  const user = auth.currentUser;
  const [status, setStatus] = useState(user ? "working" : "needs-login");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) return;
    setStatsOptOut(user.uid, true)
      .then(() => {
        if (!cancelled) setStatus("done");
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setError("Het afmelden is mislukt. Probeer het later opnieuw of pas het aan onder Account.");
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const [resubPending, setResubPending] = useState(false);
  const [resubbed, setResubbed] = useState(false);
  async function onResubscribe() {
    setResubPending(true);
    try {
      await setStatsOptOut(user.uid, false);
      setResubbed(true);
    } catch (err) {
      console.error(err);
      setError("Opnieuw aanmelden is mislukt. Probeer het later opnieuw.");
    }
    setResubPending(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-bg-sunken">
      <div className="card p-8 max-w-md w-full text-center">
        {status === "needs-login" && (
          <>
            <h1 className="text-xl font-heading font-bold mb-2">Afmelden voor statistiek-updates</h1>
            <p className="text-sm text-text-secondary mb-6">
              Log in om je af te melden. Je kunt dit daarna ook altijd aanpassen onder Account, E-mailvoorkeuren.
            </p>
            <Link to="/login" state={{ from: "/afmelden" }}>
              <Button label="Inloggen" />
            </Link>
          </>
        )}

        {status === "working" && <p className="text-sm text-text-secondary">Bezig met afmelden...</p>}

        {status === "error" && <Message variant="error" text={error} />}

        {status === "done" && !resubbed && (
          <>
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-success-subtle flex items-center justify-center">
              <CheckCircle size={28} className="text-success" />
            </div>
            <h1 className="text-xl font-heading font-bold mb-2">Je bent afgemeld</h1>
            <p className="text-sm text-text-secondary mb-6">
              Je ontvangt geen statistiek-updates meer. Van gedachten veranderd?
            </p>
            <Button
              label="Toch weer aanmelden"
              variant="secondary"
              pending={resubPending}
              loadingLabel="Bezig..."
              onClick={onResubscribe}
            />
          </>
        )}

        {resubbed && (
          <>
            <h1 className="text-xl font-heading font-bold mb-2">Je bent weer aangemeld</h1>
            <p className="text-sm text-text-secondary mb-6">Je ontvangt voortaan weer statistiek-updates.</p>
            <Link to="/">
              <Button label="Naar mijn locaties" variant="secondary" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export default Unsubscribe;
